import {jsx} from 'panel';

import {ChartType, ProjectFeatureFlag, ReportName, UserFeatureFlag} from 'common/report/constants';
import {FlowsResponse} from 'common/report/funnels/models/api-types';
import {FlowsBookmark} from 'common/report/flows/models/types';
import {ReportVisualization} from 'common/report/report-manager/visualization';
import {BaseQueryErrorType} from 'common/report/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {ChartHeaderAttrs} from 'common/widgets/chart-header/types';
import {
  getAdvancedQueryOptionItems,
  getAlignmentToggleDisabledText,
  getAnchorPositionForChartType,
  handleFlowsPopoverAction,
  updateReportAlignment,
  updateReportFromAdvancedSettings,
  updateReportHiddenEvents,
} from 'common/report/flows/util';
import {FlowsReport} from 'common/report/flows/models/report';
import {emitTrackEvent} from 'common/util/dom';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {BookmarkUpdateDetail} from 'common/report/report-manager/types';
import {
  CommonChartTypeToFlowsChartType,
  FlowsChartTypeToCommonChartType,
} from 'common/report/flows/util/bookmark-overrides';
import {AlignmentOptions} from 'common/widgets/sankey-chart/models/types';
import {SelectEl} from 'common/components/select';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {QueryReason} from 'common/report/util/query-tracking/types';

import {FlowsChartDisplayEl, PopoverActionEvent} from './charts/chart-display';
import {FlowsAdvancedSettingsEl} from './advanced-settings';
import {FlowsCardinalityControlEl} from './cardinality-control';

export class FlowsVisualization extends ReportVisualization<FlowsBookmark, FlowsResponse, BaseQueryErrorType> {
  reportName = ReportName.Flows;

  private sankeyScrollOffset = 0;

  chartDisplay() {
    return (
      <FlowsChartDisplayEl
        attrs={{loaded: true, 'sankey-scroll-offset': this.sankeyScrollOffset, elref: `chartDisplay`}}
        props={{
          bookmarkParams: this.bookmarkParams,
          queryResults: this.results,
          customEvents: this.customEvents,
        }}
        on={{
          update: (ev) =>
            this.dispatchUpdate({
              params: {...this.bookmarkParams, ...ev.detail.delta},
            }),
          updateScrollOffset: (ev) => (this.sankeyScrollOffset = ev.detail),
          popoverAction: (ev) => this.handlePopoverAction(ev),
        }}
      />
    );
  }

  headerChildrenTemplate() {
    const report = this.getReportModel();

    const alignmentToggleAllowed = this.project?.hasFeatureFlag(ProjectFeatureFlag.FlowsAlignmentToggle);
    const alignmentDisabledText = getAlignmentToggleDisabledText(report);

    return (
      <>
        <div sel='.chart-controls' attrs={{slot: `chart-controls`}}>
          {alignmentToggleAllowed && (
            <SelectEl
              sel='.alignment-selector'
              attrs={{
                disabled: !!alignmentDisabledText,
                icon: {leftIcon: AlignmentOptions[report.getOverallAlignment()].icon},
                'icon-only': true,
                'show-icon-only-down-caret': true,
                'menu-width': 200,
                sections: [
                  {
                    items: Object.values(AlignmentOptions).map((option) => ({
                      ...option,
                      isSelected: option.value === report.getOverallAlignment(),
                    })),
                  },
                ],
                tooltip: {text: alignmentDisabledText},
              }}
              on={{
                change: (ev) => {
                  const updatedReport = updateReportAlignment(report, ev.detail.item.value);
                  this.dispatchUpdate({
                    params: updatedReport.generateBookmark(),
                  });
                },
              }}
            />
          )}
          <QueryBlockEl
            sel='.hidden-events'
            attrs={{
              type: `events`,
              'exclude-custom-events': true,
              label: `Hide Events`,
              multiple: true,
              'project-id': this.attr(`project-id`),
              selected: report.visibleEvents.length ? report.visibleEvents : report.hiddenEvents,
              inverted: report.visibleEvents.length > 0,
              'selector-icon': `eye-hide`,
              'selector-type': `button`,
              'data-group-id': report.dataGroupId || ``,
              'workspace-id': this.attr(`workspace-id`),
            }}
            on={{
              select: (ev: CustomEvent<TODO>) => {
                const updatedReport = updateReportHiddenEvents(report, ev.detail);
                this.dispatchUpdate({params: updatedReport.generateBookmark()});
              },
            }}
          />
          <FlowsCardinalityControlEl
            attrs={{cardinality: report.cardinality}}
            on={{
              change: (ev) => {
                report.update({cardinality: ev.detail});
                this.dispatchUpdate({params: report.generateBookmark()});
              },
            }}
          />
        </div>

        <div sel='.advanced-controls' attrs={{slot: `advanced-chart-controls`}}>
          <FlowsAdvancedSettingsEl
            attrs={{
              items: getAdvancedQueryOptionItems({
                report,
                percentilesEnabled: this.project?.hasFeatureFlag(ProjectFeatureFlag.FlowsTimePercentiles),
                isDev: this.userDetails?.featureFlags.has(UserFeatureFlag.Dev),
              }),
            }}
            on={{
              change: (ev) => {
                const updatedReport = updateReportFromAdvancedSettings(report, ev.detail);
                this.dispatchUpdate({params: updatedReport.generateBookmark()});
              },
            }}
          />
        </div>
      </>
    );
  }

  handlePopoverAction(ev: PopoverActionEvent) {
    handleFlowsPopoverAction({
      detail: ev.detail,
      customEvents: this.customEvents,
      report: this.getReportModel(),
      updateFn: (params) => this.updateBookmarkParamsFromModel(params),
      trackFn: (eventName, props) => emitTrackEvent(this, {eventName, props}),
    });
  }

  updateBookmarkParamsFromModel(modelParams: Partial<FlowsReport>) {
    const report = this.getReportModel();
    report.update(modelParams);
    this.dispatchUpdate({params: report.generateBookmark()});
  }

  getReportModel() {
    return FlowsReport.fromBookmarkParams(this.bookmarkParams);
  }

  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    return {
      'chart-types': [ChartType.Flows, ChartType.FlowsTopPaths],
      'selected-chart-type': FlowsChartTypeToCommonChartType[this.bookmarkParams.chartType],
      'date-range': DateRange.fromBookmarkFormat(this.bookmarkParams.date_range).getAttributes(),
      'show-date-range-selector': true,
      'track-name': `flows`,
    };
  }

  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail): BookmarkUpdateDetail<FlowsBookmark> {
    const bookmarkParamUpdates: Partial<FlowsBookmark> = {};
    if (update.delta.dateRange) {
      bookmarkParamUpdates.date_range = new FullLengthDateRange(update.delta.dateRange).getBookmarkFormat();
    }

    let hasChangedChartType = false;
    if (update.delta.chartType) {
      bookmarkParamUpdates.chartType = CommonChartTypeToFlowsChartType[update.delta.chartType];
      hasChangedChartType = this.bookmarkParams.chartType !== bookmarkParamUpdates.chartType;
      bookmarkParamUpdates.anchorPosition = getAnchorPositionForChartType(update.delta.chartType);
    }

    return {
      params: {...this.bookmarkParams, ...bookmarkParamUpdates},
      queryReason: QueryReason.QbOtherUpdate,
      hasChangedChartType,
      requery: true,
    };
  }
}

export const FlowsVisualizationEl = defineAndMakeJsxFactory(`flows-visualization`, FlowsVisualization);
export const FlowsVisualizationReact = panel2react(`flows-visualization`, FlowsVisualization);
